import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const MQ_4 = '(min-width: 1100px)'
const MQ_3 = '(min-width: 800px)'
const MQ_2 = '(min-width: 576px)'

/** Slide duration — a bit slower than a snap, still snappy. */
export const HERO_CAROUSEL_TRANSITION_MS = 520

const AUTOPLAY_MS = 5500
const DRAG_AXIS_LOCK_PX = 8
const DRAG_GAP_PX = 16
/** Commit swipe if finger is faster than this (px/ms), even below distance threshold. */
const DRAG_FLICK_VELOCITY = 0.45

export function resolveHeroBannerColumns() {
  if (typeof window === 'undefined') return 4
  if (window.matchMedia(MQ_4).matches) return 4
  if (window.matchMedia(MQ_3).matches) return 3
  if (window.matchMedia(MQ_2).matches) return 2
  return 1
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function wrapIndex(value, count) {
  if (count <= 0) return 0
  return ((value % count) + count) % count
}

function shortestDelta(from, to, count) {
  if (count <= 0) return 0
  let delta = wrapIndex(to - from, count)
  if (delta > count / 2) delta -= count
  return delta
}

function getStepPx(viewportEl, cols) {
  if (!viewportEl || cols <= 0) return 0
  return (viewportEl.clientWidth + DRAG_GAP_PX) / cols
}

/**
 * Infinite Hero carousel: step = 1 tile, visible window = columns
 * (1 on mobile <576px). Track = [prev][visible…][next] with base offset -1.
 * Mouse drag + native touch swipe; vertical page scroll stays free until axis lock.
 *
 * options.columns — force visible columns (BO side-by-side desktop/mobile previews).
 */
export function useHeroBannerCarousel(banners, options = {}) {
  const forcedColumns = Number.isFinite(options.columns) ? options.columns : null
  const [columns, setColumns] = useState(forcedColumns || 4)
  const [index, setIndex] = useState(0)
  /** Extra tile-steps on top of the fixed base (-1). 0 idle; -1 after next; +1 after prev. */
  const [animShift, setAnimShift] = useState(0)
  const [transitionOn, setTransitionOn] = useState(false)
  const [dragPx, setDragPx] = useState(0)
  const [paused, setPaused] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const animatingRef = useRef(false)
  const tokenRef = useRef(0)
  const indexRef = useRef(0)
  const timeoutRef = useRef(0)
  const dragRef = useRef(null)
  const suppressClickRef = useRef(false)
  const viewportRef = useRef(null)
  const hoveringRef = useRef(false)
  const visibleColsRef = useRef(4)

  const count = banners.length
  const visibleCols = Math.min(columns, Math.max(count, 1))
  const canLoop = count > 1

  indexRef.current = index
  visibleColsRef.current = visibleCols

  useEffect(() => {
    if (forcedColumns != null) {
      setColumns(forcedColumns)
      return undefined
    }
    const apply = () => setColumns(resolveHeroBannerColumns())
    apply()
    window.addEventListener('resize', apply)
    return () => window.removeEventListener('resize', apply)
  }, [forcedColumns])

  useEffect(() => {
    if (count <= 0) {
      setIndex(0)
      return
    }
    setIndex((prev) => wrapIndex(prev, count))
  }, [count])

  const resetMotion = useCallback(() => {
    tokenRef.current += 1
    animatingRef.current = false
    window.clearTimeout(timeoutRef.current)
    setAnimShift(0)
    setTransitionOn(false)
    setDragPx(0)
    setIsDragging(false)
    dragRef.current = null
  }, [])

  useEffect(() => {
    resetMotion()
  }, [columns, count, resetMotion])

  useEffect(
    () => () => {
      tokenRef.current += 1
      window.clearTimeout(timeoutRef.current)
    },
    []
  )

  const finish = useCallback(
    (token, nextIndex) => {
      if (token !== tokenRef.current) return
      setTransitionOn(false)
      setAnimShift(0)
      setDragPx(0)
      setIndex(wrapIndex(nextIndex, count))
      animatingRef.current = false
      // Keep paused if pointer is still over the carousel (avoid autoplay fighting the user).
      if (!hoveringRef.current) setPaused(false)
    },
    [count]
  )

  const animateBy = useCallback(
    (delta) => {
      if (!count || delta === 0 || animatingRef.current) return
      if (prefersReducedMotion()) {
        setIndex((prev) => wrapIndex(prev + delta, count))
        setDragPx(0)
        return
      }

      const token = tokenRef.current + 1
      tokenRef.current = token
      animatingRef.current = true
      const target = indexRef.current + delta

      setDragPx(0)
      setTransitionOn(false)
      setAnimShift(0)

      requestAnimationFrame(() => {
        if (token !== tokenRef.current) return
        requestAnimationFrame(() => {
          if (token !== tokenRef.current) return
          setTransitionOn(true)
          setAnimShift(-delta)
        })
      })

      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(
        () => finish(token, target),
        HERO_CAROUSEL_TRANSITION_MS + 40
      )
    },
    [count, finish]
  )

  /**
   * Finish a drag from the current finger offset — no snap-back jump before the slide.
   * @param {1 | -1} direction 1 = next (finger left), -1 = prev (finger right)
   */
  const commitFromDrag = useCallback(
    (direction) => {
      if (animatingRef.current) return
      const token = tokenRef.current + 1
      tokenRef.current = token
      animatingRef.current = true
      const target = indexRef.current + direction
      const stepPx = getStepPx(viewportRef.current, visibleColsRef.current)

      setIsDragging(false)
      // Fold current drag into a pixel offset that already equals the post-shift
      // resting point minus the animated remaining distance — pure px, no % jump.
      setTransitionOn(false)
      setAnimShift(-direction)
      setDragPx((prev) => prev + direction * stepPx)

      requestAnimationFrame(() => {
        if (token !== tokenRef.current) return
        requestAnimationFrame(() => {
          if (token !== tokenRef.current) return
          setTransitionOn(true)
          setDragPx(0)
        })
      })

      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(
        () => finish(token, target),
        HERO_CAROUSEL_TRANSITION_MS + 40
      )
    },
    [finish]
  )

  const snapDragBack = useCallback(() => {
    setIsDragging(false)
    setTransitionOn(true)
    setDragPx(0)
    setAnimShift(0)
    window.clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => {
      setTransitionOn(false)
    }, HERO_CAROUSEL_TRANSITION_MS + 40)
  }, [])

  const goTo = useCallback(
    (nextIndex) => {
      if (count <= 0 || animatingRef.current) return
      const normalized = wrapIndex(nextIndex, count)
      const delta = shortestDelta(indexRef.current, normalized, count)
      if (delta === 0) return
      animateBy(delta)
    },
    [animateBy, count]
  )

  const goNext = useCallback(() => animateBy(1), [animateBy])
  const goPrev = useCallback(() => animateBy(-1), [animateBy])

  useEffect(() => {
    if (!canLoop || paused || isDragging) return undefined
    if (prefersReducedMotion()) return undefined
    const timer = window.setInterval(() => {
      if (animatingRef.current || dragRef.current) return
      animateBy(1)
    }, AUTOPLAY_MS)
    return () => window.clearInterval(timer)
  }, [animateBy, canLoop, isDragging, paused])

  const trackBanners = useMemo(() => {
    if (!count) return []
    const from = index - 1
    const length = visibleCols + 2
    const items = []
    for (let i = 0; i < length; i += 1) {
      const logical = wrapIndex(from + i, count)
      items.push({
        ...banners[logical],
        slotKey: `${logical}-${i}`,
      })
    }
    return items
  }, [banners, count, index, visibleCols])

  const [stepPx, setStepPx] = useState(0)

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return undefined

    const measure = () => {
      setStepPx(getStepPx(el, visibleColsRef.current))
    }
    measure()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [visibleCols, count, canLoop])

  const trackStyle = useMemo(() => {
    const cols = visibleCols
    const steps = -1 + animShift
    const measured = stepPx > 0
    const offsetPx = measured ? steps * stepPx + dragPx : dragPx
    return {
      '--columns': cols,
      '--gap': `${DRAG_GAP_PX}px`,
      // Prefer pure px transforms in BO preview — % + px calc jumps during drag commit.
      transform: measured
        ? `translate3d(${offsetPx}px, 0, 0)`
        : `translate3d(calc(${steps} * (100% + var(--gap)) / ${cols}${dragPx ? ` + ${dragPx}px` : ''}), 0, 0)`,
      transition:
        transitionOn && !isDragging
          ? `transform ${HERO_CAROUSEL_TRANSITION_MS}ms cubic-bezier(0.25, 0.1, 0.25, 1)`
          : 'none',
    }
  }, [animShift, dragPx, isDragging, stepPx, transitionOn, visibleCols])

  const onPause = useCallback(() => {
    hoveringRef.current = true
    setPaused(true)
  }, [])
  const onResume = useCallback(() => {
    hoveringRef.current = false
    setPaused(false)
  }, [])
  const onFocusPause = useCallback(() => setPaused(true), [])
  const onBlurResume = useCallback(() => {
    if (!hoveringRef.current) setPaused(false)
  }, [])

  const decideCommit = useCallback((dx, velocityX, step) => {
    // Lower threshold in compact BO frames so a short swipe still commits.
    const threshold = Math.max(28, step * 0.14)
    if (Math.abs(velocityX) >= DRAG_FLICK_VELOCITY) {
      return velocityX < 0 ? 1 : -1
    }
    if (Math.abs(dx) >= threshold) {
      return dx < 0 ? 1 : -1
    }
    return 0
  }, [])

  const endDragSession = useCallback(
    (clientX, timeStamp) => {
      const drag = dragRef.current
      dragRef.current = null
      if (!drag) {
        setIsDragging(false)
        if (!hoveringRef.current) setPaused(false)
        return
      }

      const dx = clientX - drag.startX
      const dt = Math.max(1, timeStamp - (drag.lastTime || drag.startTime))
      const recentDx = clientX - (drag.lastX ?? drag.startX)
      const velocityX = recentDx / dt
      const step = getStepPx(viewportRef.current, visibleColsRef.current)
      const direction = decideCommit(dx, velocityX, step)

      if (direction !== 0 && drag.axis === 'x') {
        suppressClickRef.current = true
        // Stay paused through the settle animation; finish() clears pause if needed.
        setPaused(true)
        commitFromDrag(direction)
        return
      }

      if (drag.axis === 'x' && Math.abs(dx) > 2) {
        setPaused(true)
        snapDragBack()
        window.setTimeout(() => {
          if (!hoveringRef.current) setPaused(false)
        }, HERO_CAROUSEL_TRANSITION_MS + 40)
        return
      }
      setIsDragging(false)
      setDragPx(0)
      if (!hoveringRef.current) setPaused(false)
    },
    [commitFromDrag, decideCommit, snapDragBack]
  )

  // Mouse drag via Pointer Events (ignore touch — handled below).
  useEffect(() => {
    if (!canLoop) return undefined
    const el = viewportRef.current
    if (!el) return undefined

    const onPointerDown = (event) => {
      if (event.pointerType === 'touch' || event.pointerType === 'pen') return
      if (animatingRef.current) return
      if (event.button !== 0) return
      const now = event.timeStamp || performance.now()
      dragRef.current = {
        source: 'pointer',
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: now,
        lastX: event.clientX,
        lastTime: now,
        axis: null,
      }
      suppressClickRef.current = false
    }

    const onPointerMove = (event) => {
      const drag = dragRef.current
      if (!drag || drag.source !== 'pointer' || drag.pointerId !== event.pointerId) return

      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      const now = event.timeStamp || performance.now()

      if (!drag.axis) {
        if (Math.abs(dx) < DRAG_AXIS_LOCK_PX && Math.abs(dy) < DRAG_AXIS_LOCK_PX) return
        drag.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'
        if (drag.axis === 'y') {
          dragRef.current = null
          setIsDragging(false)
          setDragPx(0)
          return
        }
        setIsDragging(true)
        setPaused(true)
        setTransitionOn(false)
        try {
          el.setPointerCapture(event.pointerId)
        } catch (_) {
          /* ignore */
        }
      }

      if (drag.axis !== 'x') return
      event.preventDefault()
      drag.lastX = event.clientX
      drag.lastTime = now
      setDragPx(dx)
    }

    const onPointerUp = (event) => {
      const drag = dragRef.current
      if (!drag || drag.source !== 'pointer' || drag.pointerId !== event.pointerId) return
      endDragSession(event.clientX, event.timeStamp || performance.now())
    }

    const onPointerCancel = (event) => {
      const drag = dragRef.current
      if (!drag || drag.source !== 'pointer' || drag.pointerId !== event.pointerId) return
      dragRef.current = null
      setPaused(false)
      snapDragBack()
    }

    const onClickCapture = (event) => {
      if (!suppressClickRef.current) return
      event.preventDefault()
      event.stopPropagation()
      suppressClickRef.current = false
    }

    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove, { passive: false })
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerCancel)
    el.addEventListener('click', onClickCapture, true)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerCancel)
      el.removeEventListener('click', onClickCapture, true)
    }
  }, [canLoop, endDragSession, snapDragBack])

  // Native touch: reliable preventDefault + vertical scroll until horizontal lock (iOS/Android).
  useEffect(() => {
    if (!canLoop) return undefined
    const el = viewportRef.current
    if (!el) return undefined

    const onTouchStart = (event) => {
      if (animatingRef.current || event.touches.length !== 1) return
      const touch = event.touches[0]
      const now = event.timeStamp || performance.now()
      dragRef.current = {
        source: 'touch',
        pointerId: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: now,
        lastX: touch.clientX,
        lastTime: now,
        axis: null,
      }
      suppressClickRef.current = false
      setPaused(true)
    }

    const onTouchMove = (event) => {
      const drag = dragRef.current
      if (!drag || drag.source !== 'touch') return
      if (event.touches.length !== 1) return
      const touch = event.touches[0]
      if (touch.identifier !== drag.pointerId) return

      const dx = touch.clientX - drag.startX
      const dy = touch.clientY - drag.startY
      const now = event.timeStamp || performance.now()

      if (!drag.axis) {
        if (Math.abs(dx) < DRAG_AXIS_LOCK_PX && Math.abs(dy) < DRAG_AXIS_LOCK_PX) return
        drag.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'
        if (drag.axis === 'y') {
          dragRef.current = null
          setIsDragging(false)
          setDragPx(0)
          setPaused(false)
          return
        }
        setIsDragging(true)
        setTransitionOn(false)
      }

      if (drag.axis !== 'x') return
      event.preventDefault()
      drag.lastX = touch.clientX
      drag.lastTime = now
      setDragPx(dx)
    }

    const onTouchEnd = (event) => {
      const drag = dragRef.current
      if (!drag || drag.source !== 'touch') return
      const touch = event.changedTouches?.[0]
      if (!touch || touch.identifier !== drag.pointerId) return
      endDragSession(touch.clientX, event.timeStamp || performance.now())
    }

    const onTouchCancel = () => {
      const drag = dragRef.current
      if (!drag || drag.source !== 'touch') return
      dragRef.current = null
      setPaused(false)
      snapDragBack()
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [canLoop, endDragSession, snapDragBack])

  return {
    columns: visibleCols,
    index,
    count,
    trackBanners,
    trackStyle,
    viewportRef,
    goTo,
    goNext,
    goPrev,
    canLoop,
    isDragging,
    onPause,
    onResume,
    onFocusPause,
    onBlurResume,
  }
}
